/**
 * ErrorBoundary — swap white-screen crashes for a friendly recovery card.
 *
 * Catches:
 *   - any React render error in its subtree
 *   - errors thrown during lifecycle methods / effects on render
 *
 * Does NOT catch:
 *   - async errors inside event handlers (React limitation)
 *   - errors in the ErrorBoundary itself
 *
 * Integration:
 *   - Wraps the root App + specific heavy routes (dashboards, lesson pages).
 *   - Reports to `window.Sentry` if present (frontend SDK may be added later).
 *   - Fires a GA/PostHog event via analytics.trackError so we see crash rates.
 */
import React from "react";
import { trackError } from "@/utils/analytics";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Best-effort reporting — never let reporting itself crash us.
    try {
      if (typeof window !== "undefined" && window.Sentry?.captureException) {
        window.Sentry.captureException(error, { extra: { componentStack: errorInfo?.componentStack } });
      }
    } catch (_) { /* ignore */ }
    try {
      trackError({
        name: error?.name || "UnknownError",
        message: (error?.message || "").slice(0, 300),
        scope: this.props.scope || "root",
      });
    } catch (_) { /* ignore */ }
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", this.props.scope || "root", error);
  }

  handleReload = () => {
    // Soft reset first — if the error was transient, this clears it.
    this.setState({ hasError: false, error: null });
    // Hard reload as a fallback, so we re-fetch the bundle in case a stale
    // deploy is the cause.
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  handleHome = () => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const scope = this.props.scope || "page";
    const title =
      scope === "lesson"
        ? "This session hit a snag."
        : scope === "dashboard"
        ? "Your dashboard hit a snag."
        : "Something went sideways.";
    const body =
      scope === "lesson"
        ? "Your progress is safe. Jump back to the dashboard and try the session again."
        : scope === "dashboard"
        ? "Your account is safe. Refresh and we'll get you back in."
        : "Your progress is safe. Refresh the page — we're tracking this automatically.";

    return (
      <div
        role="alert"
        aria-live="assertive"
        data-testid={`error-boundary-${scope}`}
        className="min-h-[60vh] flex items-center justify-center p-6 bg-gradient-to-br from-amber-50 to-yellow-50"
      >
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border-2 border-amber-200 p-6 text-center">
          <div className="text-4xl mb-3" aria-hidden="true">🍋</div>
          <h2 className="text-xl font-bold text-amber-900 mb-2">{title}</h2>
          <p className="text-sm text-gray-700 mb-5 leading-relaxed">{body}</p>
          <div className="flex gap-3 justify-center">
            <button
              type="button"
              onClick={this.handleReload}
              className="px-4 py-2 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600"
              data-testid={`error-boundary-${scope}-reload`}
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="px-4 py-2 rounded-lg bg-white border border-amber-400 text-amber-800 text-sm font-semibold hover:bg-amber-50"
              data-testid={`error-boundary-${scope}-home`}
            >
              Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
